import { SyntheticEvent } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import I18n from 'i18n-js';

import { logout } from '@lootboy/lib/actions/AuthActions';
import { requestEmailVerification } from '@lootboy/lib/actions/UserActions';
import {
  emailSelector,
  userNeedsEmailVerificationSelector,
} from '@lootboy/lib/selectors';

import Alert from '~/components/Alert';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';

import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import EnsureLoggedInContainer from '~/routes/EnsureLoggedInRoute/EnsureLoggedInContainer';
import { EMAIL_VERIFICATION_PATH, USER_DETAILS_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useAppHistory } from '~/utilities/routing';

import styles from './RequestVerificationCodePopup.module.scss';

export function RequestVerificationCodePopup(): JSX.Element {
  const alert = useAlert();
  const history = useAppHistory();
  const dispatch = useAppDispatch();

  const email = useSelector<TAppStore, string>(emailSelector);
  const userNeedsEmailVerification = useSelector<TAppStore, boolean>(
    userNeedsEmailVerificationSelector,
  );

  if (!userNeedsEmailVerification) {
    return <Redirect to={USER_DETAILS_PATH} />;
  }

  return (
    <EnsureLoggedInContainer>
      <MenuViewHeader
        title={I18n.t('MenuView_register_bc')}
        onClose={() => {
          dispatch(logout());
        }}
      />
      <section className={styles.container}>
        <AuthViewHeadline
          headline={I18n.t('MenuView_register_code_verify_headline')}
          subHeadline={
            <>
              {I18n.t('MenuView_register_code_verify')}
              <b className={styles.mail}>{email}</b>
            </>
          }
        />
      </section>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          onClick={async (event: SyntheticEvent<HTMLButtonElement>) => {
            const { currentTarget: buttonElement } = event;
            // adding a separate `useState` feels like an overkill since the whole "disabling logic"
            // can be contained within this handler
            buttonElement.disabled = true;
            try {
              await dispatch(requestEmailVerification());
              history.push(EMAIL_VERIFICATION_PATH, { isCodeRequested: true });
            } catch (error) {
              const { message } = parseLootBoyError(error);
              alert.error(
                <Alert
                  title={I18n.t(
                    'EmailVerificationView_request_new_token_failed',
                  )}
                  body={message}
                />,
              );
              buttonElement.disabled = false;
            }
          }}
        >
          {I18n.t('MenuView_register_code_pbutton')}
        </StyledButton>
      </footer>
    </EnsureLoggedInContainer>
  );
}
